/**
 * 关于webmail 登录设备（信任设备）的逻辑备注：
 * 1. 考虑到信任设备信息的存储安全性以及浏览器在存储方面的限制，
 *    后端（loginSuccessTransit.tag）会将存到cookies 中的信息移入localStorage，
 *    如果浏览器不支持localStorage，则会清空之前的token 信息
 * 2. 后端在登录流程的信任设备逻辑大致如下：
 *     1）若系统不支持登录设备记录，则不会生成或更新设备信息；
 *     2）若系统支持登录设备记录，但webmail 提交的数据中既没有accessToken 也没有deviceInfo，则不会生成或更新设备信息；
 * 3. 针对上面的描述，webmail 对登录设备信息做如下处理：
 *     1）若系统不支持登录设备记录，则不需要收集token、deviceInfo，因为没必要，而localStorage 或cookies 中如果有信息也是过时的
 *     2）若系统支持登录设备记录，且浏览器不支持localStorage，则会尝试收集cookies 中的token 字段信息用作登录设备之用，不会用作信任设备
 *     3）若系统支持登录设备记录，且浏览器支持localStorage，考虑到用户数据兼容性，旧版的webmail 会将token 写入到cookies
 *        需要尝试从localStorage、cookies中读取token 信息。无论有没有找到token 信息，都需要生成deviceInfo 让后端生成设备信息
 * */

/* eslint-disable camelcase */

;define(
    function (require) {
        var $ = require('jquery');
        var Detector = require('detector');
        var jsSHA = require('sha1');
        var Cookie = require('cookie');

        /**
         * 测试是否支持localStorage
         * */
        var supportLocalStorage = (function() {
            var test = 'cm.ls.test';
            try {
                localStorage.setItem(test, test);
                localStorage.removeItem(test);
                return true;
            } catch (e) {
                return false;
            }
        }());

        var token_cookie_prefix = {
            token: 'Coremail.token.',
            secret: 'Coremail.secret.',
            nonce: 'Coremail.nonce.'
        };

        /**
         * 从cookie 中收集token、secret、nonce
         *
         * @return {object} 信任设备需要的登录凭证
         * */
        function collectAuthMapFromCookies(uid) {
            var cookiePrefix = token_cookie_prefix;
            function removeCookieColon(value) {
                if (typeof value !== 'string' || !value.length) return value;
                if (value[0] === '"' && value[value.length - 1] === '"') {
                    value = value.slice(1, -1);
                }
                return value;
            }

            var _token, _secret, _nonce;

            _token = Cookie.get(cookiePrefix.token + encodeURI(uid));

            if (!_token) return null;

            _token = removeCookieColon(_token);

            // 若浏览器不支持localStorage，考虑到信任设备信息的安全性，不需要获取secret、nonce，只做登录设备之用
            if (!supportLocalStorage) {
                Cookie.remove(cookiePrefix.secret + encodeURI(uid), {path : '/'});
                Cookie.remove(cookiePrefix.nonce + encodeURI(uid), {path : '/'});
                _secret = false;
                _nonce = false;
            } else {
                _secret = removeCookieColon(Cookie.get(cookiePrefix.secret + encodeURI(uid)) || '');
                _nonce = removeCookieColon(Cookie.get(cookiePrefix.nonce + encodeURI(uid)) || '');
            }

            return {
                token: _token,
                secret: _secret,
                nonce: _nonce
            }
        }

        /**
         * 从LocalStorage 中收集token、secret、nonce
         *
         * @return {object} 信任设备需要的登录凭证
         * */
        function collectAuthMapFromLocalStorage(uid, timeStamp) {
            var _token, _secret, _nonce, _expires;

            var lsKey = 'Coremail.auth.' + uid;

            // 不支持localStorage
            if (!supportLocalStorage) return null;

            var authValue = localStorage.getItem(lsKey);

            // localStorage 中没有值
            if (authValue == null) return null;

            authValue = $.parseJSON(authValue);

            _token = authValue['token'];
            _secret = authValue['secret'];
            _nonce = authValue['nonce'];
            _expires = Math.ceil(authValue['expires'] / 1000);

            // 检查token 的有效时长
            if (!isNaN(_expires) && timeStamp && _expires < timeStamp) {
                localStorage.removeItem(lsKey);
                return null;
            }

            return {
                token: _token,
                secret: _secret,
                nonce: _nonce
            }
        }

        /**
         * 收集信任设备凭证的入口
         *
         * 新的实现已将数据保存在localStorage 中，为了让存在cookies 上用户的旧数据顺利过渡，会保留从cookies 中搜集数据的逻辑
         * 服务器会在成功登录后，将cookies 中的数据清空，并移至到localStorage 中。
         *
         * 对于不支持localStorage 的登录设备，服务器会向cookies 写入token，但没有secret 等信息。
         * */
        function collectAuthMap(uid) {

            var _timestamp = Math.ceil((new Date()).getTime() / 1000);
            var _token, _secret, _nonce, _signature, _hashObj;

            var authMap = collectAuthMapFromLocalStorage(uid, _timestamp) || collectAuthMapFromCookies(uid);
            if (authMap == null) return null;

            _token = authMap.token;
            _secret = authMap.secret;
            _nonce = authMap.nonce;

            if (_token && _secret && _nonce) {
                // 签名的计算查看user:login 接口文档
                _hashObj = new jsSHA('SHA-1', 'TEXT', {numRounds: 1});      // eslint-disable-line new-cap
                _hashObj.update('accessToken=' + encodeURIComponent(_token)
                                + '&nonce=' + encodeURIComponent(_nonce)
                                + '&timestamp=' + encodeURIComponent(_timestamp)
                                + '&secret=' + encodeURIComponent(_secret));
                _signature = _hashObj.getHash('B64');
            } else {
                _signature = false;
            }

            return {
                token: _token,
                secret: _secret,
                nonce: _nonce,
                signature: _signature,
                timestamp: _timestamp
            }
        }

        // 格式化登录token 凭据，并设置到对应的表单元素中
        function formatLoginToken(uid) {
            if (!uid) return null;

            var authMap = collectAuthMap(uid);

            if (authMap == null || !authMap.token) return null;
            return authMap;
        }

        // 收集当前登录设备信息
        function collectDeviceInfo() {
            var device = Detector.device;
            var os = Detector.os;
            var browser = Detector.browser;
            var unknownInfo = 'na';

            var friendlyName;
            var model = '';

            // webmail 的设备名称使用浏览器 + 版本号
            if (browser.name === unknownInfo) {
                friendlyName = unknownInfo;
            } else {
                friendlyName = browser.name;
                if (browser.version !== '-1') {
                    friendlyName += (' ' + browser.version);
                }
            }

            // 如果webmail 设备名被检测为pc，则替换为系统名
            if (device.name === unknownInfo || device.name === 'pc') {
                model = os.name;
            } else {
                model = device.name;
            }

            var deviceInfo = {
                uuid: 'webmail_' + model,
                imie: 'webmail_' + model,
                friendlyName: friendlyName,
                model: model,
                os: os.name,
                osLanguage: navigator.language,
                deviceType: 'Webmail'
            };
            return deviceInfo;
        }

        // 格式化登录设备信息，并设置到对应表单元素中
        function formatLoginDeviceInfo() {
            return collectDeviceInfo();
        }

        return {
            supportLocalStorage: supportLocalStorage,
            formatLoginToken: formatLoginToken,
            formatLoginDeviceInfo: formatLoginDeviceInfo
        }
    }
);



/*****************
 ** WEBPACK FOOTER
 ** ./web.modules/coremail.framework/src/common/assets/index_cmxt50/js/loginDeviceStore.js
 ** module id = 156
 ** module chunks = 15
 **/